import React, { useState } from "react";

import ShipeeziNewGrid, { Column, GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import BadgeStatus from "Components/Generic/BadgeStatus";
import { getDateValueInTimeZone } from "Components/Generic/MilestoneDates";
import { useAppState } from "Context/StateContext";
import { Fleet } from "Models/fleet";
import { FleetAvailability } from "Models/fleet.availability";
import { Filters, Searches } from "Models/shipeezi.table";
import { useTranslation } from "react-i18next";
import { FleetAvailabilityType } from "Util/enums/fleet";
import { getStatusBadgeColorsByAvailability } from "Util/fleetStatus";
import { getHoursDiffering } from "Util/formatDateAndTime";

import useEquipmentActions from "../../../hooks/useEquipmentActions";
import { ColumnDateShow, ColumnStatus } from "./styled";

interface AvalabilitySectionProps {
  fleet: Fleet;
}

export const AvalabilitySection = (props: AvalabilitySectionProps) => {
  const { fleet } = props;
  const { t: translate } = useTranslation();
  const { userTimezone } = useAppState();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const { getFleetAvabilities, getFleetAvabilitiesCount } = useEquipmentActions();

  const { data: availabilities, isFetching } = getFleetAvabilities({
    page,
    pageSize,
    filters,
    searches,
    equipmentId: fleet.id,
  });

  const { data: availabilitiesTotal } = getFleetAvabilitiesCount({
    searches,
    filters,
    equipmentId: fleet.id,
  });

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    const filtersKeys = Object.getOwnPropertyNames(value);

    setFilters(filtersKeys?.length ? value : undefined);
  };

  const renderDatetime = (date: string | undefined, showTime: boolean) => {
    const dateWithTimeZone = getDateValueInTimeZone(date, "dd/MM/yyyy", userTimezone?.name);
    const timeWithTimeZone = getDateValueInTimeZone(date, "HH:mm", userTimezone?.name);

    if (dateWithTimeZone) {
      return (
        <ColumnDateShow>
          <span>{dateWithTimeZone}</span>
          {showTime && !!timeWithTimeZone && <span>{timeWithTimeZone}</span>}
        </ColumnDateShow>
      );
    }

    return "-";
  };

  const columns: Column[] = [
    {
      name: "type",
      title: translate("type"),
    },
    {
      name: "dateFrom",
      title: translate("from"),
      hideSearch: true,
      formatComponent: (_, row?: FleetAvailability) => {
        return renderDatetime(row?.dateFrom, !row?.allDayAbsence);
      },
    },
    {
      name: "dateTo",
      title: translate("to"),
      hideSearch: true,
      formatComponent: (_, row?: FleetAvailability) => {
        const date = row?.dateTo || row?.dateFrom;

        return renderDatetime(date, !row?.allDayAbsence);
      },
    },
    {
      name: "hours",
      title: translate("hours"),
      hideSearch: true,
      formatComponent: (_, row?: FleetAvailability) => {
        if (row?.allDayAbsence) {
          return "-";
        }

        const startDate = row?.dateFrom ? new Date(row.dateFrom) : null;
        const endDate = row?.dateTo ? new Date(row.dateTo) : null;

        if (startDate && endDate) {
          startDate.setHours(startDate.getHours());
          startDate.setMinutes(startDate.getMinutes());

          endDate.setHours(endDate.getHours());
          endDate.setMinutes(endDate.getMinutes());

          return <span>{getHoursDiffering(startDate, endDate)}</span>;
        }

        return "-";
      },
    },
    {
      name: "createdBy",
      hideSearch: true,
      title: translate("createdBy"),
      formatText: (user: FleetAvailability["createdBy"]) => user?.fullName ?? "-",
    },
    {
      name: "status",
      hideSearch: true,
      title: translate("status"),
      formatComponent: (_, row?: FleetAvailability) => {
        if (!row) return "-";

        const { label, background, color } = getStatusBadgeColorsByAvailability(row, translate);

        return (
          <ColumnStatus>
            <BadgeStatus
              value={label}
              useExternalColor
              backgroundColor={background}
              textColor={color}
            />
          </ColumnStatus>
        );
      },
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleChangeStatus", "newRecord"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          column: "availability.type",
          name: translate("type"),
          options: Object.values(FleetAvailabilityType).map((option) => ({
            label: option,
            value: option,
          })),
        },
      ],
      onChange: handleFilter,
    },
  };

  return (
    <ShipeeziNewGrid
      data={availabilities}
      totalItems={Number(availabilitiesTotal)}
      columns={columns}
      showSelection={false}
      showSort={false}
      currentPage={page}
      loadingData={isFetching}
      pageSizeList={[10, 20, 40, 60]}
      onChangePageSize={setPageSize}
      onNextPage={setPage}
      onPreviousPage={setPage}
      onGoToPage={setPage}
      pageSize={pageSize}
      positionActionsColumn="right"
      generalActions={generalActions}
      keyExtractor={(data) => data?.id?.toString()}
      emptyData={translate("noInformationToBeShown")}
    />
  );
};
